/* Lightweight analytics hook + tracker
   - generates a stable session_id in localStorage
   - sends pageview on route change
   - sends ping every 25s while tab is visible
   - exposes a global window.gofoodTrack(name, props) for click instrumentation
*/
import { useEffect, useRef } from "react";
import { useLocation } from "react-router-dom";
import { API } from "../App";

const SS_KEY = "gofood_sess_id";

function getSessionId() {
  if (typeof window === "undefined") return "ssr";
  try {
    let id = localStorage.getItem(SS_KEY);
    if (!id) {
      id = `s_${Date.now().toString(36)}_${Math.random().toString(36).slice(2, 9)}`;
      localStorage.setItem(SS_KEY, id);
    }
    return id;
  } catch {
    return `s_${Date.now()}`;
  }
}

async function postJSON(url, body) {
  try {
    await fetch(url, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(body),
      keepalive: true,
    });
  } catch { /* swallow */ }
}

function getUserId() {
  try {
    const u = JSON.parse(localStorage.getItem("user") || "null");
    return u?.id || null;
  } catch { return null; }
}

export function useAnalytics() {
  const location = useLocation();
  const prevPath = useRef("");

  // Install global tracker once
  useEffect(() => {
    if (typeof window === "undefined") return;
    if (!window.gofoodTrack) {
      window.gofoodTrack = (name, props = {}) => {
        const session_id = getSessionId();
        const user_id = getUserId();
        const path = window.location.pathname + window.location.search;
        const type = name && name.startsWith("custom:") ? "custom" : "click";
        postJSON(`${API}/analytics/track`, {
          type,
          name: name.replace(/^custom:/, ""),
          path,
          session_id,
          user_id,
          props,
          referrer: document.referrer || null,
        });
      };
    }
  }, []);

  // Pageview on route change
  useEffect(() => {
    const path = location.pathname + location.search;
    if (path === prevPath.current) return;
    prevPath.current = path;
    const session_id = getSessionId();
    const user_id = getUserId();
    postJSON(`${API}/analytics/track`, {
      type: "pageview",
      path,
      session_id,
      user_id,
      referrer: document.referrer || null,
    });
  }, [location]);

  // Heartbeat ping every 25s while visible
  useEffect(() => {
    if (typeof window === "undefined") return;
    const session_id = getSessionId();
    const ping = () => {
      if (document.visibilityState === "visible") {
        postJSON(`${API}/analytics/ping`, { session_id });
      }
    };
    ping(); // initial
    const iv = setInterval(ping, 25000);
    document.addEventListener("visibilitychange", ping);
    return () => {
      clearInterval(iv);
      document.removeEventListener("visibilitychange", ping);
    };
  }, []);
}
